import React from "react";
import { useTranslation } from "react-i18next";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import AuthService from "src/services/authService";
import { getApiGatewayUrl, getEnv } from "src/services/configService";

import { useGetCurrentWorkspaceFdroids } from "@dashboard/repositories/api/helpers";
import { useCurrentWorkspace, useCurrentWorkspaceFeatures, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { useWorkspacePageAccess } from "@dashboard/workspaces/hooks/useWorkspacePageAccess";
import Divider from "@mui/material/Divider";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";

import { Drawer } from "./components/Drawer/Drawer";
import { HelpLinksSection } from "./components/Drawer/HelpLinksSection";
import { ListWithSubHeader } from "./components/Drawer/ListWithSubHeader";
import { CollapsibleLink, LegacyLink, RouterLink } from "./components/NavigationLinks/NavigationLinks";
import { useStyles } from "./Navigation.style";
import { useProductPageVisibility } from "./Navigation.utils";

function getLegacyUrl(path: string) {
    return getApiGatewayUrl() + "/main" + path;
}

function useIsResellerWorkspace(): boolean {
    const { data: workspaceFeatures = [] } = useCurrentWorkspaceFeatures();
    const clientManagementFeature = workspaceFeatures.find((feature) => feature.feature === "ClientManagement");
    return clientManagementFeature?.max === 1;
}

// temporary: remove hasAccessToRolloutPage when Rollouts page is ready for all users
// issue: https://gitlab.com/emteria/development/web/frontend/hub/-/work_items/1103
function hasAccessToRolloutPage(workspaceId: number | undefined): boolean {
    if (!workspaceId) {
        return false;
    }
    if (getEnv() === "production") {
        return workspaceId === 20782 || workspaceId === 50832;
    }
    return true;
}

export const Navigation = (props: React.PropsWithChildren<object>) => {
    const { classes, cx } = useStyles();

    const { effectiveWorkspaceSettings, isLoading: isWorkspaceLoading, rawTenantSettings } = useAppSettingsService();
    const { data: workspace } = useCurrentWorkspace();
    const { canViewSubscription, canAccessConfigurationTab } = useWorkspacePageAccess();
    const { t } = useTranslation(["sidepanels", "common"]);

    const { data: fdroidRepos = [] } = useGetCurrentWorkspaceFdroids();
    const repositoriesLimit = useWorkspaceFeature("FDroidRepoLimit");
    const workspaceExists = workspace !== null && workspace !== undefined && !isWorkspaceLoading;
    const isResellerWorkspace = useIsResellerWorkspace();

    const isProductPageEnabled = useProductPageVisibility();

    const isAdmin = AuthService.isUserAdmin();
    const isMerchant = AuthService.isUserMerchant();
    const isDeveloper = AuthService.isUserDeveloper();
    const isSupporter = AuthService.isUserSupporter();
    const isAccountant = AuthService.isUserAccountant();
    const isAllAccessRole = AuthService.isUserInAnyAccessRole();

    const canViewAppRepositories =
        rawTenantSettings.features.pages.repositoriesPageEnabled && (repositoriesLimit !== 0 || !!fdroidRepos.length);

    // The navbar scrolls internally only when its (possibly expanded) content overflows the window height,
    // so a hidden scrollbar never masks entries further down the list.
    const { ref: navigationBoxRef, contentOverflows } = useContainerOverflow(true, [], `.${classes.navigationContent}`);

    return (
        <Drawer
            navigationContent={
                <div
                    className={cx(classes.navigationBox, !contentOverflows && classes.navigationBoxNoScroll)}
                    ref={navigationBoxRef}
                >
                    <div className={classes.navigationContent}>
                        <ListWithSubHeader
                            title={workspaceExists ? workspace?.name || t("sidepanels:navigationMenu.dashboard") : undefined}
                        >
                            <RouterLink
                                title={t("sidepanels:navigationMenu.deviceEnrollment")}
                                startIcon="fa-solid fa-plus"
                                to="/dashboard/enrollment"
                                resetOnReselect
                                condition={effectiveWorkspaceSettings.isEnrollmentPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.dashboard")}
                                startIcon="fa-solid fa-chart-line"
                                to="/dashboard/overview"
                                condition={effectiveWorkspaceSettings.isOverviewPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.devices")}
                                startIcon="fa-solid fa-laptop"
                                to="/dashboard/devices"
                                condition={effectiveWorkspaceSettings.isDevicesPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.groups")}
                                startIcon="fa-solid fa-folder-tree"
                                to="/dashboard/groups"
                                condition={effectiveWorkspaceSettings.isGroupsPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.provisioning")}
                                startIcon="fa-solid fa-sliders"
                                to="/dashboard/provisioning"
                                condition={effectiveWorkspaceSettings.isProvisioningPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.downloads")}
                                startIcon="fa-regular fa-floppy-disk"
                                to="/dashboard/downloads"
                                condition={effectiveWorkspaceSettings.isDownloadsPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.products")}
                                startIcon="fa-solid fa-microchip"
                                to="/dashboard/products"
                                condition={isProductPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.files")}
                                startIcon="fa-regular fa-file"
                                to="/dashboard/files"
                                condition={effectiveWorkspaceSettings.isFilesPageEnabled}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.rollouts")}
                                startIcon="fa-solid fa-paper-plane"
                                to="/dashboard/rollouts"
                                condition={
                                    effectiveWorkspaceSettings.isRolloutPageEnabled && hasAccessToRolloutPage(workspace?.id)
                                }
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.appRepositories")}
                                startIcon="fa-solid fa-cubes"
                                to="/dashboard/repositories"
                                condition={canViewAppRepositories}
                            />
                            <RouterLink
                                title={t("sidepanels:navigationMenu.universalLicenses")}
                                startIcon="fa-solid fa-ticket"
                                to="/dashboard/licenses"
                                condition={effectiveWorkspaceSettings.isUniversalLicensesPageEnabled}
                            />
                        </ListWithSubHeader>
                        {workspaceExists ? (
                            <>
                                <Divider />
                                <ListWithSubHeader title={t("sidepanels:navigationMenu.administration")}>
                                    <CollapsibleLink
                                        title={t("sidepanels:navigationMenu.workspace")}
                                        startIcon="fa-solid fa-briefcase"
                                        condition={rawTenantSettings.features.pages.workspacePageEnabled}
                                    >
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.workspaceIntro")}
                                            to="/account/workspaces"
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.workspaceMembers")}
                                            to="/account/workspaces/members"
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.workspaceSubscription")}
                                            to="/account/workspaces/subscription"
                                            condition={canViewSubscription}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.workspaceConfiguration")}
                                            to="/account/workspaces/configuration"
                                            condition={canAccessConfigurationTab}
                                        />
                                    </CollapsibleLink>
                                    <CollapsibleLink
                                        title={t("sidepanels:navigationMenu.clients")}
                                        startIcon="fa-solid fa-clipboard-list"
                                        condition={isResellerWorkspace}
                                    >
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.clientsOverview")}
                                            to="/clients/overview"
                                        />
                                    </CollapsibleLink>
                                    <CollapsibleLink
                                        title={t("sidepanels:navigationMenu.adminTitle")}
                                        startIcon="fa-regular fa-user"
                                        condition={isAllAccessRole}
                                    >
                                        <RouterLink title={"Accounts"} to="/admin/accounts" condition={isAdmin} />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.announcements")}
                                            to="/admin/announcements"
                                            condition={isMerchant}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.builds")}
                                            to="/admin/builds"
                                            condition={isAdmin}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.certificate")}
                                            to="/admin/certificate"
                                            condition={isAdmin}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.customers")}
                                            href={getLegacyUrl("/Admin/Customers")}
                                            condition={isMerchant}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.email")}
                                            to="/admin/email"
                                            condition={isAdmin}
                                        />
                                        <RouterLink title={t("common:label.events")} to="/admin/events" condition={isAdmin} />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.expirations")}
                                            to="/admin/expirations"
                                            condition={isMerchant || isAccountant}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.fdroid")}
                                            to="/admin/fdroid-repos"
                                            condition={isAdmin || isSupporter}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.files")}
                                            to="/admin/files"
                                            condition={isAdmin}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.groups")}
                                            to="/admin/groups"
                                            condition={isAdmin || isMerchant}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.invoices")}
                                            href={getLegacyUrl("/Admin/Invoices")}
                                            condition={isMerchant}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.licenses")}
                                            href={getLegacyUrl("/Admin/Licenses")}
                                            condition={isMerchant}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.memberships")}
                                            to="/admin/members"
                                            condition={isAdmin}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.oauth")}
                                            to="/admin/clients"
                                            condition={isAdmin}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.products")}
                                            to="/admin/products"
                                            condition={isDeveloper}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.releases")}
                                            href={getLegacyUrl("/Admin/Releases")}
                                            condition={isDeveloper}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.roles")}
                                            to="/admin/roles"
                                            condition={isDeveloper}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.security")}
                                            href={getLegacyUrl("/Admin/Security")}
                                            condition={isSupporter}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.subscriptions")}
                                            href={getLegacyUrl("/Admin/Subscriptions")}
                                            condition={isMerchant}
                                        />
                                        <LegacyLink
                                            title={t("sidepanels:navigationMenu.admin.vouchers")}
                                            href={getLegacyUrl("/Admin/Vouchers")}
                                            condition={isMerchant}
                                        />
                                        <RouterLink
                                            title={t("sidepanels:navigationMenu.admin.workspaces")}
                                            to="/admin/workspaces"
                                            condition={isMerchant || isAccountant}
                                        />
                                    </CollapsibleLink>
                                </ListWithSubHeader>
                            </>
                        ) : null}
                        <Divider />
                        <ListWithSubHeader title={t("sidepanels:navigationMenu.personal")}>
                            <CollapsibleLink title={t("sidepanels:navigationMenu.account")} startIcon="fa-solid fa-circle-user">
                                <RouterLink title={t("sidepanels:navigationMenu.accountPortalSettings")} to="/account/settings" />
                                <RouterLink
                                    title={t("sidepanels:navigationMenu.accountProfileData")}
                                    to="/account/settings/profile"
                                />
                                <RouterLink
                                    title={t("sidepanels:navigationMenu.accountSecurity")}
                                    to="/account/settings/security"
                                />
                                <RouterLink
                                    title={t("sidepanels:navigationMenu.accountBillingAddress")}
                                    to="/account/settings/billing"
                                />
                                <RouterLink title={t("sidepanels:navigationMenu.accountApiKey")} to="/account/settings/apikey" />
                                <RouterLink title={t("sidepanels:navigationMenu.logout")} to="/logout" />
                            </CollapsibleLink>
                            <HelpLinksSection />
                        </ListWithSubHeader>
                    </div>
                </div>
            }
        >
            {props.children}
        </Drawer>
    );
};
